// AntD and MDI
import Icon from '@mdi/react'
import { Divider, Select, Tooltip } from 'antd'
import {
	mdiTruck,
	mdiPhone,
	mdiAccount,
	mdiMapMarker,
	mdiFileDownloadOutline,
	mdiTextBoxRemoveOutline,
	mdiFileEditOutline,
	mdiLanDisconnect,
	mdiConnection,
	mdiDotsVertical,
	mdiVectorLink,
	mdiClockTimeEightOutline,
	mdiFileCloudOutline,
	mdiFileCheckOutline,
} from '@mdi/js'
import { LoadingOutlined } from '@ant-design/icons'
import { Form, Menu, Dropdown, Spin } from 'antd'

// Utils
import { SegmentsEnum, ContainerDimensionsEnum, BiltyStatusEnum, UserTypesEnum, orderCategoryEnum } from './Enums'
import moment from 'moment'
import { formatPhoneNumber, momentDate } from './Functions'

/* ============================================================================================== */
/* ================================ BILTY MANAGEMENT COLUMNS ==================================== */
/* ============================================================================================== */

export const BLOrderNoColumn = (segment, tabStatus) => {
	return {
		title: <div className='pl-4'>{'Order' + (segment === 'Import' ? ' & BL' : segment === 'Export' ? ' & CRO' : '') + ' INFO'}</div>,
		key: 'OrderNoColumn',
		sorter: (a, b) => tabStatus === 'issuance' && a?.orderNumber - b?.orderNumber,
		defaultSortOrder: 'descend',
		sortDirections: ['ascend', 'descend', 'ascend'],
		width: '16%',
		render: (_, record) => {
			const initial = SegmentsEnum[segment].initial

			return (
				<div className='flex flex-col justify-around pl-6'>
					<div className='text-primary-800 text-base font-semibold mb-1'>{record?.subOrderNumber && initial + '-' + record?.subOrderNumber}</div>
					{record?.documentNumber && <div className='font-bold text-sm text-black-200 mb-1'>{record?.documentNumber}</div>}
					{['Shifting', 'Destuffing', 'DoubleTwenty']?.includes(record?.orderCategory) && (
						<div className={`order-type-pill ${orderCategoryEnum[record?.orderCategory]?.background}`}>{orderCategoryEnum[record?.orderCategory]?.text}</div>
					)}
					{record?.preferences?.lotOrder && <div className='order-type-pill bg-yellow-300'>Lot Order</div>}
				</div>
			)
		},
	}
}

export const ContainerDetailsColumn = {
	title: 'CONTAINER INFO',
	key: 'ContainerDetailsColumn',
	width: '15%',
	render: (_, record) => {
		const containerNumber = record?.containerNumber
		const dimension = record?.dimension
		return (
			<div className='flex flex-col justify-around pl-2'>
				<div className='font-semibold text-base mb-1'>{containerNumber}</div>
				<div className='text-sm font-medium mb-1'>{ContainerDimensionsEnum[dimension]?.text}</div>
				{record?.totalCount && (
					<Tooltip
						placement='right'
						color='#676767'
						title={
							<div className='flex flex-row items-center w-fit text-xs !p-0.5'>
								<span className='text-green-400 font-bold mr-1'>{parseInt(record.totalCount) - parseInt(record?.movedCount)}</span>
								<span className='text-grey-50 font-medium mr-1'>{record?.movedUnits?.toLowerCase()} remain of</span>
								<span className='font-bold mr-1'>{record.totalCount}</span>
							</div>
						}
					>
						<div className='flex flex-row rounded cursor-pointer bg-grey-200 px-2 py-1 w-fit h-fit'>
							<span className='mr-2'>
								<Icon path={mdiVectorLink} size={0.7} color='#676767' />
							</span>
							<div className='font-semibold text-xs'>Partially Moved</div>
						</div>
					</Tooltip>
				)}
			</div>
		)
	},
}

export const RouteColumn = {
	title: 'ROUTE',
	key: 'RouteColumn',
	width: '30%',
	render: (_, record) => {
		const orderLocations = record?.orderLocations
		return (
			<div className='flex flex-col justify-center pl-2'>
				{orderLocations?.['Empty Container Pickup']?.[0]?.Location &&
					orderLocations?.['Empty Container Pickup']?.map((location) => (
						<div className='flex flex-row items-center mt-1'>
							<div className='rounded-full p-1 bg-black-200 mr-4'>
								<Icon className='text-white' size={0.6} path={mdiMapMarker} />
							</div>
							<p className='w-10/12 font-light text-sm'>{location?.Location?.formattedAddress + ', ' + location?.Location?.additionalDetails}</p>
						</div>
					))}
				{orderLocations?.['loading']?.map((location) => (
					<div className='flex flex-row items-center mt-2'>
						<div className='rounded-full p-1 bg-primary-800 mr-4'>
							<Icon className='text-white' size={0.6} path={mdiMapMarker} />
						</div>
						<p className='w-10/12 font-light text-sm'>{location?.Location?.formattedAddress + ', ' + location?.Location?.additionalDetails}</p>
					</div>
				))}
				{orderLocations?.['dropoff']?.map((location) => (
					<div className='flex flex-row items-center mt-2'>
						<div className='rounded-full p-1 bg-red-400 mr-4'>
							<Icon className='text-white' size={0.6} path={mdiMapMarker} />
						</div>
						<p className='w-10/12 font-light text-sm'>{location?.Location?.formattedAddress + ', ' + location?.Location?.additionalDetails}</p>
					</div>
				))}
				{orderLocations?.['Empty Container Dropoff']?.[0]?.Location &&
					orderLocations?.['Empty Container Dropoff']?.map((location) => (
						<div className='flex flex-row items-center'>
							<div className='rounded-full p-1 bg-black-200 mr-4'>
								<Icon className='text-white' size={0.6} path={mdiMapMarker} />
							</div>
							<p className='w-10/12 font-light text-sm'>{location?.Location?.formattedAddress + ', ' + location?.Location?.additionalDetails}</p>
						</div>
					))}
			</div>
		)
	},
}

export const ClientColumn = {
	title: 'CLIENT INFO',
	key: 'ClientColumn',
	width: '22%',
	render: (_, record) => {
		const businessName = record?.businessName
		const shippingLine = record?.shippingLine
		return (
			<div className='flex flex-col justify-center pl-2'>
				<div className='font-semibold text-base mb-2'>{businessName}</div>
				{record?.type !== 'LongHaul' && (
					<div className='font-normal text-sm'>
						Shipping Line: <span className='text-grey-300 font-medium'>{shippingLine || '---'}</span>
					</div>
				)}
			</div>
		)
	},
}

export const JobDetailsColumn = (tabStatus, collection) => {
	return {
		title: <div className='pl-2'>JOB DETAILS</div>,
		key: 'jobDetails',
		sorter: (a, b) =>
			tabStatus === 'cancelled'
				? parseInt(a?.bilty?.[0]?.biltyCancelledAt) - parseInt(b?.bilty?.[0]?.biltyCancelledAt)
				: tabStatus === 'inProgress'
				? parseInt(a?.bilty?.[0]?.biltyCreatedAt) - parseInt(b?.bilty?.[0]?.biltyCreatedAt)
				: tabStatus === 'completed' && parseInt(a?.bilty?.[0]?.biltyCompletedAt) - parseInt(b?.bilty?.[0]?.biltyCompletedAt),
		defaultSortOrder: 'descend',
		sortDirections: ['ascend', 'descend', 'ascend'],
		width: collection === 'biltyTracking' ? '20%' : '12%',
		render: (_, record) => {
			const orderCategory = [record?.orderCategory]?.flat()
			const lotOrder = record?.subOrders?.[0]?.preferences?.lotOrder
			return (
				<Tooltip title={<div className='!p-0.5 w-fit text-xs font-semibold'>View Job Details</div>} placement='topLeft' color='#676767'>
					<div className='pl-4 cursor-pointer'>
						<div className='text-primary-800 font-bold text-base underline underline-offset-[3px] mb-1'>J-{record?.jobNumber}</div>
						{orderCategory?.some((value) => ['Shifting', 'Destuffing', 'DoubleTwenty']?.includes(value)) && (
							<div className={`order-type-pill ${orderCategoryEnum[orderCategory[0]]?.background}`}>{orderCategoryEnum[orderCategory[0]]?.text}</div>
						)}
						{lotOrder && <div className='order-type-pill bg-yellow-300'>Lot Order</div>}
					</div>
				</Tooltip>
			)
		},
	}
}

export const BiltyInformationColumn = (collection, biltyOpUsers) => {
	return {
		title: 'BILTY DETAILS',
		key: 'BiltyInformationColumn',
		width: collection === 'biltyTracking' ? '30%' : '26%',
		render: (_, record) => {
			const bilty = record?.bilty || []
			return (
				// change this
				<div className='flex flex-col justify-center items-start pl-2'>
					<div className='flex flex-row mb-2'>
						{bilty?.length !== 0 && bilty?.some((bilty) => bilty?.biltyCreatedAt) ? (
							<>
								{[...bilty]?.slice(0, 2)?.map((bilty) => {
									const biltyNumber = bilty?.biltyNumber
									const biltyDocument = bilty?.biltyDocument
									const halfDoneBiltyDocument = bilty?.halfDoneBiltyDocument
									return (
										<div className='w-fit bg-grey-200 rounded-full px-2 mr-1 py-0.5 cursor-pointer'>
											<a
												target='_blank'
												rel='noreferrer'
												onClick={(e) => e.stopPropagation()}
												className='text-black-300 text-sm font-semibold'
												href={halfDoneBiltyDocument?.[0] || biltyDocument?.[0]}
											>
												{biltyNumber}
											</a>
										</div>
									)
								})}
								{bilty?.length > 2 && (
									<div className='font-medium text-sm text-primary-800 bg-primary-50 py-0.5 px-2 rounded-full self-center'>+{bilty?.length - 2}</div>
								)}
							</>
						) : (
							<>
								<div className='text-sm font-normal text-red-400 underline underline-offset-[3px] mb-1'>Bilty Not Created</div>{' '}
							</>
						)}
					</div>
					<div className='flex flex-row items-center'>
						<div className='flex flex-row mr-2.5 items-center'>
							<Icon className='text-black-300 rounded-full p-1 bg-black-75 mr-1' size={0.9} path={mdiAccount} />
							<div className='font-normal text-xs text-black-300'>
								{bilty?.[0]?.isCancelled
									? `${biltyOpUsers?.[bilty?.[0]?.biltyCancelledBy]?.firstName} ${biltyOpUsers?.[bilty?.[0]?.biltyCancelledBy]?.lastName}`
									: bilty?.[0]?.isInformationComplete
									? `${biltyOpUsers?.[bilty?.[0]?.biltyCompletedBy]?.firstName} ${biltyOpUsers?.[bilty?.[0]?.biltyCompletedBy]?.lastName}`
									: bilty?.[0]?.biltyCreatedBy
									? `${biltyOpUsers?.[bilty?.[0]?.biltyCreatedBy]?.firstName} ${biltyOpUsers?.[bilty?.[0]?.biltyCreatedBy]?.lastName}`
									: '---'}
							</div>
						</div>
						<div className='flex flex-row items-center'>
							<Icon className='text-black-300 rounded-full p-1 bg-black-75 mr-1' size={0.9} path={mdiClockTimeEightOutline} />
							<div className='font-bold text-xs text-black-300'>
								{bilty?.[0]?.isCancelled
									? momentDate(bilty?.[0]?.biltyCancelledAt)
									: bilty?.[0]?.isInformationComplete
									? momentDate(bilty?.[0]?.biltyCompletedAt)
									: bilty?.[0]?.biltyCreatedBy
									? momentDate(bilty?.[0]?.biltyCreatedAt)
									: '---'}
							</div>
						</div>
					</div>
				</div>
			)
		},
	}
}

export const OrderDetailsColumn = (segment, collection) => {
	return {
		title: `${segment === 'Import' ? 'CONTAINER' : 'ORDER'} DETAILS`,
		key: 'OrderDetailsColumn',
		width: collection === 'biltyTracking' ? '25%' : '20%',
		render: (_, record) => {
			const subOrders = record?.subOrders || []
			const initial = SegmentsEnum[segment].initial
			return (
				<div className='flex flex-wrap sticky items-center justify-start pl-2'>
					{[...subOrders]?.slice(0, 4)?.map((subOrder) => (
						<Tooltip
							color='#676767'
							title={
								(subOrder?.partialMatched || subOrder?.completeMatched) && (
									<div className='flex flex-row items-center w-fit text-xs !p-0.5'>
										<span className='text-green-400 font-bold mr-1'>{subOrder.movementCount}</span>
										<span className='text-grey-50 font-medium mr-1'>out of</span>
										<span className='font-bold mr-1'>{subOrder.totalCount}</span>
										<span className='text-grey-50 font-medium'>{subOrder?.movedUnits?.toLowerCase()}</span>
									</div>
								)
							}
						>
							<div
								className={`font-semibold text-sm text-black-300 rounded-full px-3 py-0.5 border ${
									subOrder?.partialMatched ? 'border-dashed cursor-pointer' : 'border-solid'
								} border-primary-800 mr-2 mb-2`}
							>
								{segment === 'Import' ? subOrder?.containerNumber : `${initial}-${subOrder?.subOrderNumber}`}
							</div>
						</Tooltip>
					))}
					{subOrders?.length > 4 && (
						<div className='font-medium text-sm text-primary-800 bg-primary-50 py-0.5 px-2 rounded-full self-center'>+{subOrders?.length - 4}</div>
					)}
				</div>
			)
		},
	}
}

export const DriverDetailsColumn = {
	title: 'DRIVER DETAILS',
	key: 'DriverDetailsColumn',
	render: (_, record) => {
		const driver = record?.Driver
		const vehicle = record?.Vehicle
		return (
			<div className='flex flex-col justify-center pl-2'>
				<div className='flex flex-row items-center pb-1 mb-2'>
					<Icon className='text-black-300 rounded-full p-1 bg-black-75 mr-2' size={0.9} path={mdiAccount} />
					<div className='font-medium text-sm'>{driver?.name || '---'}</div>
				</div>
				<div className='flex flex-row items-center'>
					<div className='flex flex-row items-center mr-2'>
						<Icon className='text-black-300 rounded-full p-1 bg-black-75 mr-2' size={0.9} path={mdiTruck} />
						<span className='font-medium text-sm'>{vehicle?.registrationNumber?.toUpperCase()}</span>
					</div>
					<div className='flex flex-row items-center'>
						<Icon className='text-black-300 rounded-full p-1 bg-black-75 mr-2' size={0.9} path={mdiPhone} />
						<div className='text-sm text-black-300'>{driver?.phoneNumber}</div>
					</div>
				</div>
			</div>
		)
	},
}

export const ActionColumn = (segment, navigator, tabStatus, selectedOrders, user) => {
	return {
		title: '',
		key: 'ActionColumn',
		width: '19%',
		render: (_, record) => {
			const biltyCreated = record?.bilty?.[0]?.biltyCreatedAt
			const lotOrder = record?.subOrders?.[0]?.preferences?.lotOrder
			const showCreatedBilty = lotOrder && !biltyCreated && tabStatus === 'inProgress'
			const DropdownMenu = (
				<Menu>
					{record?.bilty?.[0]?.biltyType === 'systemGenerated' && (
						<Menu.Item key='0'>
							<button
								className='FlexRow'
								onClick={(e) => {
									e.stopPropagation()
									navigator(`/biltyManagement/${segment}/${tabStatus}/job/${record?._id}/printBilty`)
								}}
							>
								<Icon className='Icon GrayColor' path={mdiFileDownloadOutline} />
								<p className='HalfMarginLeft'>Print Bilty</p>
							</button>
						</Menu.Item>
					)}
					{record?.bilty?.length !== 0 &&
						!record?.bilty?.every((bilty) => bilty?.initialKantaWeight && bilty?.initialKantaCharges && bilty?.initialKantaDocument?.[0]) &&
						tabStatus !== 'issuance' && (
							<Menu.Item key='1'>
								<button
									className='flex flex-row'
									onClick={(e) => {
										e.stopPropagation()
										navigator(`/biltyManagement/${segment}/${tabStatus}/job/${record?._id}/missingKanta`)
									}}
								>
									<Icon className='Icon GrayColor' path={mdiFileEditOutline} />
									<p className='HalfMarginLeft'>Add Missing Kanta</p>
								</button>
							</Menu.Item>
						)}
					{!record?.subOrders?.[0]?.preferences?.lotOrder && tabStatus === 'inProgress' && user?.userType === 'BiltySuperAdmin' && (
						<Menu.Item key='2'>
							<button
								className='FlexRow'
								onClick={(e) => {
									e.stopPropagation()
									navigator(`/biltyManagement/${segment}/${tabStatus}/job/${record?._id}/cancelBilty`)
								}}
							>
								<Icon className='Icon  text-red-400' path={mdiTextBoxRemoveOutline} />
								<p className='HalfMarginLeft'>Cancel Bilty</p>
							</button>
						</Menu.Item>
					)}
					{record?.bilty?.length !== 0 &&
						record?.bilty?.[0]?.biltyType === 'systemGenerated' &&
						record?.bilty?.some((bilty) => !bilty?.halfDoneBiltyDocument) && (
							<Menu.Item key='3'>
								<button
									className='flex flex-row'
									onClick={(e) => {
										e.stopPropagation()
										navigator(`/biltyManagement/${segment}/${tabStatus}/job/${record?._id}/saveBilty`)
									}}
								>
									<Icon className='Icon GrayColor' path={mdiFileCloudOutline} />
									<p className='HalfMarginLeft'>Generate & Save Bilty</p>
								</button>
							</Menu.Item>
						)}
					{tabStatus === 'issuance' && (
						<Menu.Item key='4'>
							<button
								disabled={record?.totalCount === null}
								className={`flex flex-row ${record?.totalCount === null ? 'cursor-not-allowed' : 'cursor-pointer'}`}
								onClick={(e) => {
									e.stopPropagation()
									navigator(`/biltyManagement/${segment}/${tabStatus}/orders/${record?._id}/markOrderComplete`)
								}}
							>
								<Icon className='Icon GrayColor' path={mdiFileCheckOutline} />
								<p className='HalfMarginLeft'>Mark Order Complete</p>
							</button>
						</Menu.Item>
					)}
				</Menu>
			)

			return (
				<div className='flex justify-end items-center mr-4'>
					<button
						disabled={selectedOrders?.length >= 2}
						className='flex flex-row items-center btn-action'
						onClick={(e) => {
							e.stopPropagation()
							navigator(
								`/biltyManagement/${segment}/${tabStatus}/${tabStatus === 'issuance' ? 'orders' : 'job'}/${record?._id}/${
									BiltyStatusEnum[showCreatedBilty ? 'issuance' : tabStatus].biltyActionModal
								}`
							)
						}}
					>
						<Icon className='mr-2' size={1} path={BiltyStatusEnum[showCreatedBilty ? 'issuance' : tabStatus].biltyActionIcon} />
						<div>{BiltyStatusEnum[showCreatedBilty ? 'issuance' : tabStatus].biltyActionText}</div>
					</button>
					{((tabStatus === 'issuance' &&
						segment === 'Import' &&
						['szaidi@bridgelinxpk.com', 'zshah@bridgelinxpk.com', 'mumohsin@bridgelinxpk.com', 'muhusman@bridgelinxpk.com']?.includes(
							user?.profileInfo?.email
						)) ||
						tabStatus === 'inProgress' ||
						tabStatus === 'completed') && (
						<Dropdown
							overlay={DropdownMenu}
							trigger={['click']}
							onClick={(e) => e.stopPropagation()}
							className='flex relative items-center justify-center cursor-pointer ml-3 h-[40px] w-[40px] bg-grey-200 rounded'
						>
							<div>
								{record?.bilty?.length !== 0 &&
									!record?.bilty?.every((bilty) => bilty?.initialKantaWeight && bilty?.initialKantaCharges && bilty?.initialKantaDocument?.[0]) &&
									tabStatus !== 'issuance' && <div className='absolute top-0 right-0 rounded-full bg-red-400 p-1 m-[-3px]'></div>}
								<Icon className='text-black-300' path={mdiDotsVertical} size={1} />
							</div>
						</Dropdown>
					)}
				</div>
			)
		},
	}
}

export const LongHaulFreightColumn = {
	title: 'FREIGHT INFO',
	key: 'LongHaulFreightColumn',
	width: '15%',
	render: (_, record) => {
		const freightWeight = record?.shipmentInfo?.freightWeight
		const freightType = record?.shipmentInfo?.freightType
		const vehicleInfo = record?.vehicleInfo
		return (
			<div className='pl-2'>
				{freightType && (
					<div className='font-semibold capitalize'>
						Comodity:
						<span className='font-normal'> {freightType}</span>
					</div>
				)}
				{freightWeight && (
					<div className='font-semibold'>
						Weight:
						<span className='font-normal'> {freightWeight} Tons</span>
					</div>
				)}
				{vehicleInfo?.vehicleType && (
					<div className='font-semibold'>
						Vehicle:
						<span className='font-normal'> {ContainerDimensionsEnum[vehicleInfo?.vehicleType]?.long}</span>
					</div>
				)}
			</div>
		)
	},
}

export const BiltyCancelReasonColumn = {
	title: 'CANCEL REASON',
	key: 'BiltyCancelReasonColumn',
	render: (_, record) => {
		return (
			<div className='flex flex-col justify-center w-fit rounded-xl px-5 py-1 bg-red-100 text-red-500'>
				<div className='font-semibold'>{record?.bilty?.[0]?.biltyCancelReason}</div>
			</div>
		)
	},
}

/* ============================================================================================== */
/* ================================== USER MANAGEMENT COLUMNS =================================== */
/* ============================================================================================== */

export const UserNameColumn = {
	title: 'Name',
	key: 'UserDetailsColumn',
	render: (_, record) => {
		const userName = record?.userName
		// const phoneNumber = record?.phoneNumber
		// const email = record?.email
		return <div className='text-sm font-medium pl-2'>{userName}</div>
	},
}

export const UserEmailColumn = {
	title: 'Email',
	key: 'UserEmailColumn',
	render: (_, record) => {
		const email = record?.email
		return <div className='text-sm font-medium pl-2'>{email || '---'}</div>
	},
}

export const UserPhoneNumberColumn = {
	title: 'Phone Number',
	key: 'UserPhoneNumberColumn',
	render: (_, record) => {
		const phoneNumber = record?.phoneNumber
		return <div className='text-sm font-medium pl-2'>{phoneNumber || '---'}</div>
	},
}

export const UserRegionsColumn = {
	title: 'Regions',
	key: 'UserRegionsColumn',
	render: (_, record) => {
		const regions = [record?.regions]?.flat()
		return <div className='text-sm font-medium pl-2'>{regions?.length === 0 ? '---' : regions?.map((region) => region?.label)?.join(', ')}</div>
	},
}

export const RolesColumn = ({ userTypes, promiseLoading }) => {
	return {
		title: 'User Role',
		key: 'RolesColumn',
		render: (_, record) => {
			return promiseLoading[record?._id] ? (
				<Spin style={{ display: 'flex', justifyContent: 'center' }} size='medium' indicator={<LoadingOutlined style={{ fontSize: 24 }} />} />
			) : (
				<Form.Item className='!w-[220px] !m-3' key={[record?._id, 'biltyUserTypeId']} name={[record?._id, 'biltyUserTypeId']}>
					<Select className={`RolesSelectField BorderRadiusRound BoldFont CenterAlign`} value={record?.biltyUserTypeId} defaultValue={record?.biltyUserTypeId}>
						{userTypes.map((userType, index) => (
							<Select.Option key={index} value={userType?.key}>
								<div className='FlexRowBox'>
									<div className='BoldFont CenterAlign'>{UserTypesEnum[userType?.value]?.text}</div>
								</div>
							</Select.Option>
						))}
					</Select>
				</Form.Item>
			)
		},
	}
}

/* ============================================================================================== */
/* ================================== TRACKER MANAGEMENT COLUMNS ================================ */
/* ============================================================================================== */

export const TrackerNameColumn = {
	key: 'TrackerNameColumn',
	title: 'TRACKER ID & PROVIDER',
	width: '15%',
	render: (_, record) => (
		<div className='flex flex-row items-center pl-2'>
			<div className='font-[600] text-[14px] text-black-400 pr-2 w-24'>{record?.name}</div>
			<Divider type='vertical' />
			<div className='font-medium text-[14px] text-black-300 pl-2'>{record?.provider || ''}</div>
		</div>
	),
}
export const TrackerConnectedColumn = {
	key: 'TrackerConnectedColumn',
	title: <div className='text-center'>STATUS</div>,
	width: '10%',
	render: (_, record) => (
		<div className={`${record?.isConnected ? 'bg-green-100 text-green-400' : 'bg-red-50 text-red-400'} rounded py-1 px-2 w-fit mx-auto`}>
			<div className='font-bold text-[12px] '>{record?.isConnected ? 'Connected' : 'Not Connected'}</div>
		</div>
	),
}

export const TrackerLaneColumn = {
	key: 'TrackerLaneColumn',
	title: 'LANE',
	render: (_, record) =>
		record?.lane ? (
			<div className='flex flex-row text-[14px] items-center pl-2'>
				<div className='font-[600] text-center align-middle'>{(record?.lane?.from || 'None') + ' --> ' + (record?.lane?.to || 'None')}</div>
			</div>
		) : (
			<div className='pl-2'>---</div>
		),
}

export const TrackerVehicleInfoColumn = {
	key: 'TrackerVehicleInfoColumn',
	title: 'VEHICLE AND DRIVER',
	render: (_, record) =>
		record?.vehicle ? (
			<div className='flex flex-row items-center'>
				<div className='font-[600] text-[14px] text-black-400 pr-2'>{record?.vehicle}</div>
				<Divider type='vertical' />
				<div className='font-medium text-[14px] text-black-300 pl-2 '>{formatPhoneNumber(record?.driver)}</div>
			</div>
		) : (
			<div className='text-center align-middle'>---</div>
		),
}

export const TrackerAddedDateColumn = {
	key: 'TrackerAddedDateColumn',
	title: 'INSTALLATION DATE',
	width: '12%',
	render: (_, record) => (
		<div className='font-medium text-[14px] text-black-300 text-center align-middle'>
			{record?.installationDate ? moment(record?.installationDate)?.format('DD/MM/YYYY') : '---'}
		</div>
	),
}

export const TrackerAvailabilityColumn = (promiseLoading) => {
	return {
		key: 'TrackerAvailabilityColumn',
		title: <div className='text-center'>AVAILABILITY</div>,
		render: (_, record) => {
			let status = record?.status || 'deleted'
			const statuses = [
				{ value: 'deleted', key: 'deleted', title: 'Inactive' },
				{ value: 'active', key: 'active', title: 'Active' },
				{ value: 'underRepair', key: 'underRepair', title: 'Under Repair' },
				{ value: 'missing', key: 'missing', title: 'Missing' },
			]
			return promiseLoading[record?._id] ? (
				<Spin style={{ display: 'flex', justifyContent: 'center' }} size='medium' indicator={<LoadingOutlined style={{ fontSize: 24 }} />} />
			) : (
				<Form.Item className='!m-3' key={[record?._id, 'status']} name={[record?._id, 'status']}>
					<Select
						defaultValue={status}
						loading={promiseLoading[record?._id]}
						className='TrackerSelectField font-normal text-sm w-fit min-w-[200px] max-w-[240px] text-center'
					>
						{statuses.map((status) => (
							<Select.Option key={status.key} value={status.value}>
								<div className='flex flex-row items-center justify-center'>
									<div className='text-center font-semibold'>{status.title}</div>
								</div>
							</Select.Option>
						))}
					</Select>
				</Form.Item>
			)
		},
	}
}

export const TrackerJobStatusColumn = {
	key: 'TrackerJobStatusColumn',
	width: '10%',
	title: <div className='text-center'>JOB STATUS</div>,
	render: (_, record) => (
		<div
			className={`${
				record?.jobStatus && (record?.jobStatus === 'Job Ongoing' ? 'bg-green-100 text-green-400' : 'bg-red-50 text-red-400')
			} rounded py-1 px-2 w-fit mx-auto`}
		>
			<div className='font-bold text-[12px] '>{record?.jobStatus || '---'}</div>
		</div>
	),
}

export const DisconnectTrackerColumn = (handleFinish, promiseLoading) => ({
	key: 'TrackerJobStatusColumn',
	title: <div className='text-center'>STATUS ACTION</div>,
	width: '13%',
	render: (_, record) => {
		return promiseLoading[record?._id] ? (
			<Spin style={{ display: 'flex', justifyContent: 'center' }} size='medium' indicator={<LoadingOutlined style={{ fontSize: 24 }} />} />
		) : (
			<button
				className={` ${record?.isConnected ? 'bg-red-400' : 'bg-green-400'} text-white rounded py-1 px-2 w-fit mx-auto flex flex-row items-center`}
				onClick={() => handleFinish(record)}
			>
				<Icon size={0.8} path={record?.isConnected ? mdiLanDisconnect : mdiConnection} />
				<div className='!font-medium !text-xs pl-2'>{record?.isConnected ? 'Disconnect Tracker' : 'Connect Tracker'}</div>
			</button>
		)
	},
})

/* ================================== BILTY MANAGEMENT COLUMNS =========================================== */

export const biltyManagementColumns = ({ segment, tabStatus, navigator, biltyOpUsers, selectedOrders, user }) => [
	...(tabStatus === 'issuance'
		? [
				BLOrderNoColumn(segment, tabStatus),
				segment === 'LongHaul' ? LongHaulFreightColumn : ContainerDetailsColumn,
				RouteColumn,
				ClientColumn,
				ActionColumn(segment, navigator, tabStatus, selectedOrders, user),
		  ]
		: ['inProgress', 'completed']?.includes(tabStatus)
		? [
				JobDetailsColumn(tabStatus),
				BiltyInformationColumn(tabStatus, biltyOpUsers),
				OrderDetailsColumn(segment),
				DriverDetailsColumn,
				ActionColumn(segment, navigator, tabStatus, selectedOrders, user),
		  ]
		: [JobDetailsColumn(tabStatus), BiltyInformationColumn(tabStatus, biltyOpUsers), DriverDetailsColumn, BiltyCancelReasonColumn]),
]

/* ================================== BILTY TRACKING COLUMNS =========================================== */
export const biltyTrackingColumns = ({ segment, biltyOpUsers, tabStatus, collection }) => [
	JobDetailsColumn(tabStatus, collection),
	BiltyInformationColumn(collection, biltyOpUsers),
	OrderDetailsColumn(segment, collection),
	DriverDetailsColumn,
]

/* ================================== USER MANAGEMENT COLUMNS =========================================== */
export const userManagementColumns = ({ userTypes, promiseLoading }) => [
	UserNameColumn,
	UserEmailColumn,
	UserPhoneNumberColumn,
	UserRegionsColumn,
	RolesColumn({ userTypes, promiseLoading }),
]

/* ================================== TRACKER MANAGEMENT COLUMNS =========================================== */
export const trackerManagementColumns = ({ user, handleFinish, promiseLoading }) => [
	TrackerNameColumn,
	TrackerConnectedColumn,
	...(user?.profileInfo?.email === 'szaidi@bridgelinxpk.com' ? [DisconnectTrackerColumn(handleFinish, promiseLoading)] : []),
	TrackerAddedDateColumn,
	...(user?.profileInfo?.email !== 'szaidi@bridgelinxpk.com' ? [TrackerJobStatusColumn] : []),
	TrackerLaneColumn,
	TrackerVehicleInfoColumn,
	TrackerAvailabilityColumn(promiseLoading),
]
